// components/QuestionnaireDivorceModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { ClipboardIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";

/**
 * ✅ Sans Supabase / Sans API
 * -> La modal lit directement les données depuis `affaire` (props).
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - affaire: object (ex: args.affaire)
 * - title: string
 */
export default function QuestionnaireDivorceModal({
  open,
  onClose,
  affaire,
  title = "Questionnaire divorce",
}) {
  //console.log("affaire dans modal divorce", affaire);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!open) setCopied(null);
  }, [open]);

  const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";

  const copyToClipboard = async (field, value) => {
    try {
      await navigator.clipboard.writeText(String(value ?? ""));
      setCopied(field);
      setTimeout(() => setCopied(null), 1200);
    } catch (err) {
      console.error("copy error", err);
    }
  };

  // ✅ champs + labels + mapping d'affichage (quand c'est une valeur codée)
  const items = useMemo(() => {
    const a = affaire || {};

    const valeurImmoLabel = (() => {
      if (!a.valeur_immo) return null;
      if (a.valeur_immo === "moins30") return "Moins de 30 000 €";
      if (a.valeur_immo === "30-200") return "Entre 30 000 et 200 000 €";
      if (a.valeur_immo === "200-500") return "Entre 200 000 et 500 000 €";
      if (a.valeur_immo === "plus500") return "Plus de 500 000 €";
      return a.valeur_immo; // fallback
    })();

    const souhaitBienLabel = (() => {
      if (!a.souhait_bien) return null;
      if (a.souhait_bien === "vendre") return "Vendre le bien";
      if (a.souhait_bien === "indivision") return "Rester en indivision";
      if (a.souhait_bien === "rachat") return "Rachat de part";
      if (a.souhait_bien === "ne_sais_pas") return "Ne sais pas encore";
      return a.souhait_bien; // fallback
    })();

    return [
      { key: "bien_immo", label: "Bien immobilier", value: a.bien_immo },
      { key: "valeur_immo", label: "Valeur du bien immobilier", value: valeurImmoLabel },
      { key: "credit_en_cours", label: "Y a-t-il un crédit en cours ?", value: a.credit_en_cours },
      { key: "contrat_mariage", label: "Y a-t-il un contrat de mariage ?", value: a.contrat_mariage },
      { key: "regime_matrimonial", label: "Régime matrimonial choisi", value: a.regime_matrimonial },
      { key: "souhait_bien", label: "Souhait de l'utilisateur", value: souhaitBienLabel },
    ];
  }, [affaire]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />

      <div className="fixed inset-0 p-4 flex items-center justify-center">
        <DialogPanel className="pointer-events-auto w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {title}
              </DialogTitle>
            </div>

            <button
              onClick={onClose}
              className="rounded-md p-2 text-gray-400 hover:text-gray-600"
              aria-label="Fermer"
              title="Fermer"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto p-6">
            <h5 className="flex-auto truncate text-sm font-semibold leading-6 text-gray-700 mb-3">
              <b>{title}</b>
            </h5>

            <div className="bg-white rounded-lg">
              <section className="mb-6">
                <div className="overflow-hidden rounded-xl ring-1 ring-black/5 bg-white divide-y divide-gray-200">
                  {items.map((it) => (
                    <dl key={it.key} className="divide-y divide-gray-200">
                      <div className="grid grid-cols-1 sm:grid-cols-12 items-start gap-3 p-4 sm:p-5">
                        <dt className="sm:col-span-4 text-sm font-medium text-gray-700">
                          {it.label}
                        </dt>

                        <dd className="sm:col-span-7 text-sm text-black font-bold whitespace-pre-wrap break-words">
                          {hasValue(it.value) ? (
                            String(it.value)
                          ) : (
                            <span className="!font-regular text-gray-400">
                              Non renseigné
                            </span>
                          )}
                        </dd>

                        {/* COPY BTN */}
                        <div className="sm:col-span-1 flex sm:justify-end">
                          <button
                            type="button"
                            onClick={() => copyToClipboard(it.key, it.value)}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            title="Copier"
                          >
                            {copied === it.key ? (
                              <>
                                <ClipboardDocumentCheckIcon className="h-4 w-4 text-green-600" />
                                Copié
                              </>
                            ) : (
                              <>
                                <ClipboardIcon className="h-4 w-4 text-gray-500" />
                                Copier
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </dl>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}